import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Popover,
  Row,
  Spin,
  Typography,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { getDashboardCalendar } from "../../services/dashboardApi";

const { Title, Text } = Typography;

const DAYS_SUN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_MON = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const DOT_COLORS = {
  green: "#22c55e",
  red: "#ef4444",
  orange: "#f97316",
  blue: "#3b82f6",
};

function resolveColor(color) {
  return DOT_COLORS[color] || color || "#586888";
}

function pad(n) {
  return String(n).padStart(2, "0");
}

/**
 * Build a flat array of cell descriptors for a calendar month.
 * startDay: 'Sun' (default, dashboard) | 'Mon' (timesheet / ISO week)
 * Filler cells have { day: null, cur: false }
 * Real cells have { day: <number>, cur: true }
 */
function buildCells(year, month, startDay = "Sun") {
  const rawDow     = new Date(year, month, 1).getDay(); // 0=Sun … 6=Sat
  const offset     = startDay === "Mon" ? (rawDow === 0 ? 6 : rawDow - 1) : rawDow;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push({ day: null, cur: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, cur: true });
  while (cells.length % 7 !== 0) cells.push({ day: null, cur: false });
  return cells;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function parseDescription(desc) {
  if (!desc) return { text: "", author: null };
  const m = desc.match(/\n\s*-\s*([^\n]+)\s*$/);
  if (m) return { text: desc.slice(0, m.index).trim(), author: m[1].trim() };
  return { text: desc.trim(), author: null };
}

function buildEvents(days = []) {
  const map = {};
  days.forEach((day) => {
    map[day.date] = (day.events || []).map((e) => {
      const { text, author } = parseDescription(e.description);
      return { color: e.color, label: e.label, title: e.title, text, author };
    });
  });
  return map;
}

function EventsPopoverContent({ events }) {
  return (
    <div style={{ maxWidth: 280, maxHeight: 260, overflowY: "auto" }}>
      {events.map((e, i) => (
        <div
          key={i}
          style={{
            display: "flex", gap: 8, padding: "6px 0",
            borderBottom: i < events.length - 1 ? "1px solid #f0f0f0" : "none",
          }}
        >
          <span
            className="calendar-dot"
            style={{ background: resolveColor(e.color), marginTop: 6, flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, display: "block" }}>{e.title || e.label}</Text>
            {e.text && (
              <Text style={{ fontSize: 12, color: "#555", whiteSpace: "pre-line", wordBreak: "break-word" }}>
                {e.text}
              </Text>
            )}
            {e.author && (
              <Text style={{ fontSize: 11, color: "#999", display: "block", marginTop: 2 }}>
                — {e.author}
              </Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * CalendarCard — dual-mode calendar component following the same isControlled
 * pattern as ListView.
 *
 * ── DASHBOARD / AUTO MODE (default — no config props needed) ─────────────────
 *   <CalendarCard userIds={[...]} />
 *   Fetches data via getDashboardCalendar(), renders coloured event dots + popover.
 *
 * ── CONTROLLED / CONFIG MODE (pass any of the config props) ─────────────────
 *   <CalendarCard
 *     days={flatDayArray}         // external day data; skips internal fetch
 *     startDay="Mon"              // 'Sun' (default) | 'Mon' for ISO work week
 *     renderDayContent={fn}       // (dateKey, dayObj) => JSX; replaces dot render
 *     onDayClick={fn}             // (dateKey, dayObj) => void; replaces internal popover
 *     headerExtra={<JSX />}       // controls injected into card header (beside the month label)
 *     legend={[{label,color,icon}]} // overrides API-returned legend
 *     readonly={false}            // disables cell interaction
 *     className="my-class"        // extra CSS class on the Card wrapper
 *     monthLabel="Jan 2026"       // overrides auto-computed month label
 *     onPrevMonth={fn}            // external prev-month handler (replaces internal state)
 *     onNextMonth={fn}            // external next-month handler
 *   />
 *
 * Controlled mode is detected when `days` is not undefined OR `renderDayContent`
 * or `onDayClick` is provided — same isControlled pattern as ListView.
 */
export default function CalendarCard({
  // ── dashboard (auto) props ──────────────────────────────────────────────────
  userIds = [],

  // ── config / controlled props ───────────────────────────────────────────────
  days             = undefined,   // external day array → skips internal fetch
  startDay         = "Sun",       // 'Sun' | 'Mon'
  renderDayContent = undefined,   // (dateKey, dayObj) => JSX
  onDayClick       = undefined,   // (dateKey, dayObj) => void
  headerExtra      = undefined,   // JSX injected in header (next to the month label)
  legend           = undefined,   // override legend; [{label, color, icon}]
  readonly         = false,
  className        = "",
  monthLabel       = undefined,   // override displayed month+year label
  year             = undefined,   // controlled grid year (use with `month`)
  month            = undefined,   // controlled grid month 1-12 (use with `year`)
  onPrevMonth      = undefined,   // external nav handler
  onNextMonth      = undefined,
  prevDisabled     = false,       // grey out + disable the Previous month button
  nextDisabled     = false,       // grey out + disable the Next month button
}) {
  // isControlled: any config prop signals controlled mode (no internal fetch)
  const isControlled = days !== undefined || renderDayContent !== undefined || onDayClick !== undefined;

  const today  = new Date();
  const [internalView, setInternalView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  // When the caller drives the month (year + month provided), the grid MUST follow
  // it so the generated date-keys (e.g. "2026-06-01") match the `days` data. Without
  // this the grid stays on the current month and every cell renders "No log".
  const view = (year != null && month != null)
    ? { year: Number(year), month: Number(month) - 1 }
    : internalView;
  const [events, setEvents]           = useState({});
  const [apiLegend, setApiLegend]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [activePopover, setActivePopover] = useState(null);

  const userKey = JSON.stringify(userIds);
  const DAYS    = startDay === "Mon" ? DAYS_MON : DAYS_SUN;

  // ── Internal data fetch (dashboard / auto mode only) ────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDashboardCalendar({ userIds });
      if (res?.status && res?.data) {
        setApiLegend(res.data.legend || []);
        setEvents(buildEvents(res.data.days || []));
      } else {
        setApiLegend([]);
        setEvents({});
      }
    } catch (err) {
      console.error("CalendarCard fetch error:", err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  useEffect(() => {
    if (isControlled) return; // skip when caller manages data
    fetchData();
  }, [isControlled, fetchData]);

  useEffect(() => {
    window.addEventListener("dashboard-calendar:refresh", fetchData);
    return () => window.removeEventListener("dashboard-calendar:refresh", fetchData);
  }, [fetchData]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const handlePrev = onPrevMonth ?? (() =>
    setInternalView((v) => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 })
  );
  const handleNext = onNextMonth ?? (() =>
    setInternalView((v) => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 })
  );

  // ── Grid ────────────────────────────────────────────────────────────────────
  const cells = buildCells(view.year, view.month, startDay);
  const weeks  = chunk(cells, 7);

  // Build dateKey → dayObj lookup from the external days array (controlled mode)
  const externalDayMap = {};
  if (days !== undefined) {
    (days || []).forEach((d) => { if (d?.date) externalDayMap[d.date] = d; });
  }

  const displayLegend = legend ?? apiLegend;
  const displayLabel  = monthLabel ?? `${MONTHS[view.month]} ${view.year}`;

  return (
    <Card
      className={`calendar-card${className ? ` ${className}` : ""}`}
      title={
        <div className="calendar-header-title">
          <CalendarOutlined />
          <Title level={5} style={{ margin: 0 }}>{displayLabel}</Title>
          {/* headerExtra sits beside the month label, not the nav buttons — the
              only current consumer (Timesheet's Select/Quick Log controls) reads
              as part of "what month am I acting on", not calendar navigation. */}
          {headerExtra}
        </div>
      }
      extra={
        <div className="calendar-nav" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Tooltip title="Previous month">
            <Button type="text" icon={<LeftOutlined />} size="small" onClick={handlePrev} disabled={prevDisabled} />
          </Tooltip>
          <Tooltip title="Next month">
            <Button type="text" icon={<RightOutlined />} size="small" onClick={handleNext} disabled={nextDisabled} />
          </Tooltip>
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Day-name header row */}
        <Row gutter={[6, 0]} className="calendar-day-names">
          {DAYS.map((d) => (
            <Col key={d} flex="1" className="calendar-day-name">{d}</Col>
          ))}
        </Row>

        {/* Calendar grid — one Row per week */}
        <div className="calendar-weeks">
          {weeks.map((week, wi) => (
            <Row key={wi} gutter={[6, 6]} align="top" className="calendar-week-row">
              {week.map((cell, ci) => {
                // ── Filler cell (prev/next month overflow) ─────────────────
                if (!cell.cur || cell.day === null) {
                  return (
                    <Col key={ci} flex="1">
                      <div className="calendar-cell calendar-cell--filler" />
                    </Col>
                  );
                }

                const dateKey = `${view.year}-${pad(view.month + 1)}-${pad(cell.day)}`;
                const isToday =
                  cell.day === today.getDate() &&
                  view.month === today.getMonth() &&
                  view.year === today.getFullYear();

                // ── Controlled mode: delegate cell content to renderDayContent ─
                if (isControlled) {
                  const dayObj = externalDayMap[dateKey] ?? null;

                  // If caller provides a full custom renderer, render it directly
                  // in the Col without an extra wrapper (lets TimesheetDayCell
                  // be the outermost element — same pattern as field.render in ListView)
                  if (renderDayContent) {
                    return (
                      <Col key={ci} flex="1">
                        {renderDayContent(dateKey, dayObj, { isToday, readonly })}
                      </Col>
                    );
                  }

                  // Caller provided onDayClick but no custom renderer — show date
                  // number in a clickable cell
                  return (
                    <Col key={ci} flex="1">
                      <div
                        className={[
                          "calendar-cell",
                          isToday ? "today" : "",
                          readonly ? "calendar-cell--readonly" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => !readonly && onDayClick?.(dateKey, dayObj)}
                        style={{ cursor: readonly ? "default" : "pointer" }}
                      >
                        <div className="calendar-cell-inner">
                          <Text className="calendar-cell-number">{cell.day}</Text>
                        </div>
                      </div>
                    </Col>
                  );
                }

                // ── Dashboard / auto mode: dots + popover ─────────────────
                const dayEvents = events[dateKey] || [];
                const dotColors = [...new Set(dayEvents.map((e) => e.color))].slice(0, 3);

                return (
                  <Col key={ci} flex="1">
                    <div className={`calendar-cell${isToday ? " today" : ""}`}>
                      <div className="calendar-cell-inner">
                        <Text className="calendar-cell-number">{cell.day}</Text>
                        {dayEvents.length > 0 && (
                          <Popover
                            open={activePopover === dateKey}
                            trigger="click"
                            content={<EventsPopoverContent events={dayEvents} />}
                            placement="topRight"
                            destroyTooltipOnHide
                            onOpenChange={(visible) =>
                              setActivePopover(visible ? dateKey : null)
                            }
                          >
                            <div className="calendar-cell-dots">
                              {dotColors.map((color, j) => (
                                <span
                                  key={j}
                                  className="calendar-dot"
                                  style={{ background: resolveColor(color) }}
                                />
                              ))}
                            </div>
                          </Popover>
                        )}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ))}
        </div>

        {/* Legend */}
        {displayLegend.length > 0 && (
          <div className="calendar-legend">
            {displayLegend.map(({ label, color, icon }) => (
              <div key={label} className="calendar-legend-item">
                {icon
                  ? <span className="calendar-legend-badge" style={{ background: resolveColor(color) }}>{icon}</span>
                  : <span className="calendar-legend-dot" style={{ background: resolveColor(color) }} />
                }
                <Text>{label}</Text>
              </div>
            ))}
          </div>
        )}
      </Spin>
    </Card>
  );
}
